/**
 * Offline Photo Cache Service
 *
 * Fetches photo blobs from URLs and stores them in IndexedDB so images
 * remain available when the device is offline (pre-signed S3 URLs expire).
 *
 * Used by contact, credential, and member cache services during sync.
 */

import { db, CachedPhoto } from './db';

type EntityType = CachedPhoto['entity_type'];

/** Map of active object URLs so we can revoke them to avoid memory leaks. */
const objectUrlCache = new Map<string, string>();

function cacheKey(entityType: EntityType, entityId: string): string {
  return `${entityType}:${entityId}`;
}

/**
 * Fetch a photo from a URL and store the blob in IndexedDB.
 * Skips silently if the URL is falsy or the fetch fails.
 */
async function cachePhoto(
  entityType: EntityType,
  entityId: string,
  photoUrl: string
): Promise<void> {
  if (!photoUrl) return;

  try {
    const response = await fetch(photoUrl);
    if (!response.ok) return;

    const blob = await response.blob();
    if (blob.size === 0) return;

    const record: CachedPhoto = {
      id: cacheKey(entityType, entityId),
      entity_type: entityType,
      entity_id: entityId,
      blob,
      content_type: blob.type || 'image/jpeg',
      size_bytes: blob.size,
      cached_at: new Date().toISOString(),
    };

    await db.cachedPhotos.put(record);
  } catch {
    // Non-critical — photo simply won't be available offline
  }
}

/**
 * Batch-cache photos for multiple entities.
 * Processes in parallel with a concurrency limit to avoid flooding the network.
 */
async function cachePhotos(
  entityType: EntityType,
  items: Array<{ id: string; photoUrl?: string | null }>
): Promise<number> {
  const withPhotos = items.filter((i) => i.photoUrl);
  if (withPhotos.length === 0) return 0;

  // Check which photos we already have cached
  const existingIds = new Set(
    (
      await db.cachedPhotos
        .where('entity_type')
        .equals(entityType)
        .primaryKeys()
    )
  );

  const toFetch = withPhotos.filter(
    (i) => !existingIds.has(cacheKey(entityType, i.id))
  );

  if (toFetch.length === 0) return 0;

  // Fetch up to 5 at a time
  const CONCURRENCY = 5;
  let cached = 0;

  for (let i = 0; i < toFetch.length; i += CONCURRENCY) {
    const batch = toFetch.slice(i, i + CONCURRENCY);
    const results = await Promise.allSettled(
      batch.map((item) => cachePhoto(entityType, item.id, item.photoUrl!))
    );
    cached += results.filter((r) => r.status === 'fulfilled').length;
  }

  return cached;
}

/**
 * Get an object URL for a cached photo blob. Returns null if not cached.
 * The caller should NOT revoke the URL — it's managed by this service.
 */
async function getPhotoUrl(
  entityType: EntityType,
  entityId: string
): Promise<string | null> {
  const key = cacheKey(entityType, entityId);

  // Return memoised object URL if we already created one
  if (objectUrlCache.has(key)) {
    return objectUrlCache.get(key)!;
  }

  const record = await db.cachedPhotos.get(key);
  if (!record) return null;

  const url = URL.createObjectURL(record.blob);
  objectUrlCache.set(key, url);
  return url;
}

/**
 * Remove cached photo for a specific entity.
 */
async function removePhoto(
  entityType: EntityType,
  entityId: string
): Promise<void> {
  const key = cacheKey(entityType, entityId);
  const existing = objectUrlCache.get(key);
  if (existing) {
    URL.revokeObjectURL(existing);
    objectUrlCache.delete(key);
  }
  await db.cachedPhotos.delete(key);
}

/**
 * Clear all cached photos for a given entity type (or all types).
 */
async function clearCache(entityType?: EntityType): Promise<void> {
  // Revoke all object URLs
  for (const [key, url] of objectUrlCache) {
    if (!entityType || key.startsWith(`${entityType}:`)) {
      URL.revokeObjectURL(url);
      objectUrlCache.delete(key);
    }
  }

  if (entityType) {
    await db.cachedPhotos.where('entity_type').equals(entityType).delete();
  } else {
    await db.cachedPhotos.clear();
  }
}

/**
 * Get total size of cached photos in bytes.
 */
async function getCacheSize(entityType?: EntityType): Promise<number> {
  let records: CachedPhoto[];
  if (entityType) {
    records = await db.cachedPhotos
      .where('entity_type')
      .equals(entityType)
      .toArray();
  } else {
    records = await db.cachedPhotos.toArray();
  }
  return records.reduce((sum, r) => sum + r.size_bytes, 0);
}

export const photoCacheService = {
  cachePhoto,
  cachePhotos,
  getPhotoUrl,
  removePhoto,
  clearCache,
  getCacheSize,
};
